import { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useApplicantContext } from '../contexts/ApplicantContext';
import { Card, Button, Input, Textarea, HelperText } from './ui';
import { Check, Copy, Upload, X } from 'lucide-react';
import {
  DEFAULT_DISCOVERY_PROMPT_OPTIONS,
  DISCOVERY_PROMPT_ITEM_COUNT_MAX,
  DISCOVERY_PROMPT_ITEM_COUNT_MIN,
  applicantFirstName,
  buildDiscoveryPrompt,
  type DiscoveryPromptOptions,
  type NamedAward,
} from '../lib/discoveryPrompt';
import type { Scholarship, TargetSchool } from '@scholarship-app/types';

// =============================================================================
// DiscoveryPromptPanel (v0.2.50)
//
// The Inbox's companion to "Import AI Discoveries". Builds a tailored
// scholarship-discovery prompt from the selected applicant's profile, target
// schools, and the household's already-known awards (tracked, dismissed,
// pending in the inbox), which the family copies into THEIR OWN AI chat
// (ChatGPT / Claude / Gemini — any). The AI's JSON answer is then pasted back
// through the import form. No AI calls happen inside the app — discovery
// stays zero-cost to operate.
//
// Flow:  [Generate discovery prompt] → copy → run in your AI → [Import AI
//        Discoveries] (paste)
//
// Data the prompt is built from is loaded once when the panel opens (three
// getDocs — target_schools, scholarships, dismissed — plus the profile the
// ApplicantContext already holds and the inbox items the page already has).
// The prompt re-renders live as the options change.
//
// Privacy: buildDiscoveryPrompt works from a PII allow-list — the prompt
// never contains the legal name, date of birth, SSN, street address, ZIP,
// phone, or email. Financial info (income bracket / EFC) is behind a toggle.
// =============================================================================

interface Props {
  /** Inbox items still pending review (new / reviewing / snoozed) — excluded from the AI's results. */
  pendingInbox: NamedAward[];
  onOpenImport: () => void;
  onClose: () => void;
}

export function DiscoveryPromptPanel({ pendingInbox, onOpenImport, onClose }: Props) {
  const { membership } = useAuth();
  const { selectedApplicantId, selectedApplicant, profile } = useApplicantContext();
  const [targetSchools, setTargetSchools] = useState<TargetSchool[]>([]);
  const [tracked, setTracked] = useState<NamedAward[]>([]);
  const [dismissed, setDismissed] = useState<NamedAward[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [options, setOptions] = useState<DiscoveryPromptOptions>(DEFAULT_DISCOVERY_PROMPT_OPTIONS);
  // The count field keeps its raw text while typing (a controlled number
  // input that clamps on every keystroke turns "1" then "0" into 40); the
  // clamped number is committed when in range and again on blur.
  const [itemCountText, setItemCountText] = useState(String(DEFAULT_DISCOVERY_PROMPT_OPTIONS.itemCount));
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load the three collections the prompt's school list + exclusions come from.
  useEffect(() => {
    if (!db || !membership || !selectedApplicantId) {
      setLoading(false);
      return;
    }
    const fs = db;
    const hid = membership.householdId;
    const aid = selectedApplicantId;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    (async () => {
      try {
        const [schoolsSnap, scholarshipsSnap, dismissedSnap] = await Promise.all([
          getDocs(collection(fs, 'households', hid, 'applicants', aid, 'target_schools')),
          getDocs(collection(fs, 'households', hid, 'applicants', aid, 'scholarships')),
          getDocs(collection(fs, 'households', hid, 'dismissed')),
        ]);
        if (cancelled) return;

        const schools = schoolsSnap.docs
          .map((d) => d.data() as TargetSchool)
          .filter((s) => !s.archived)
          .sort((a, b) => {
            if (a.tier !== b.tier) return a.tier === 'primary' ? -1 : 1;
            return (a.rank ?? 0) - (b.rank ?? 0);
          });
        setTargetSchools(schools);

        // Every tracked award (any status, incl. archived) is "known" — the AI
        // shouldn't re-suggest it even if it was rejected or expired.
        setTracked(
          scholarshipsSnap.docs
            .map((d) => d.data() as Scholarship)
            .map((s) => ({ name: s.name, sponsor: s.sponsor }))
        );
        setDismissed(
          dismissedSnap.docs.map((d) => {
            const data = d.data() as { name?: string; sponsor?: string };
            return { name: data.name ?? '', sponsor: data.sponsor ?? '' };
          })
        );
      } catch (e) {
        if (cancelled) return;
        console.error('[DiscoveryPromptPanel] load failed:', e);
        setLoadError('Could not load target schools and tracked scholarships: ' + (e instanceof Error ? e.message : String(e)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [membership, selectedApplicantId]);

  const firstName = applicantFirstName(profile, selectedApplicant?.display_name ?? '');

  const prompt = useMemo(() => {
    if (loading) return '';
    return buildDiscoveryPrompt({
      applicantDisplayName: selectedApplicant?.display_name ?? '',
      profile,
      targetSchools,
      tracked,
      dismissed,
      pending: pendingInbox,
      options,
      today: new Date(),
    });
  }, [loading, selectedApplicant, profile, targetSchools, tracked, dismissed, pendingInbox, options]);

  const wordCount = useMemo(() => (prompt ? prompt.trim().split(/\s+/).length : 0), [prompt]);

  async function copyPrompt() {
    if (!prompt) return;
    setCopyError(null);
    let ok = false;
    try {
      await navigator.clipboard.writeText(prompt);
      ok = true;
    } catch {
      // Clipboard API unavailable (older browser / insecure context): fall
      // back to selecting the textarea and using the legacy copy command.
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.select();
        try {
          ok = document.execCommand('copy');
        } catch {
          ok = false;
        }
      }
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      // The text is selected in the textarea for a manual copy.
      setCopyError('Copy didn’t work in this browser — the prompt is selected below; press Ctrl+C (⌘C on Mac).');
    }
  }

  function onItemCountChange(raw: string) {
    setItemCountText(raw);
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n) && n >= DISCOVERY_PROMPT_ITEM_COUNT_MIN && n <= DISCOVERY_PROMPT_ITEM_COUNT_MAX) {
      setOptions((o) => ({ ...o, itemCount: n }));
    }
  }

  function onItemCountBlur() {
    const n = parseInt(itemCountText, 10);
    const clamped = Number.isNaN(n)
      ? options.itemCount
      : Math.min(DISCOVERY_PROMPT_ITEM_COUNT_MAX, Math.max(DISCOVERY_PROMPT_ITEM_COUNT_MIN, n));
    setItemCountText(String(clamped));
    setOptions((o) => ({ ...o, itemCount: clamped }));
  }

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-medium text-ink">Generate an AI discovery prompt</h2>
          <HelperText>
            Built from {firstName}&rsquo;s profile, target schools, and what you already track. Run it in your own
            AI (ChatGPT, Claude, Gemini&hellip;), then paste the JSON it returns into Import AI Discoveries.
          </HelperText>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} title="Close">
          <X size={14} />
        </Button>
      </div>

      {/* The three steps */}
      <ol className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
        {[
          ['1', 'Copy the prompt below.'],
          ['2', 'Paste it into your AI and run it.'],
          ['3', 'Paste the JSON it returns via Import AI Discoveries.'],
        ].map(([n, text]) => (
          <li key={n} className="flex items-start gap-2 rounded border border-line bg-cream px-3 py-2">
            <span className="shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-forest-700 text-white font-medium">
              {n}
            </span>
            <span className="text-soft">{text}</span>
          </li>
        ))}
      </ol>

      {/* Privacy note — keep this truthful to what buildDiscoveryPrompt renders. */}
      <p className="text-xs text-soft rounded border border-line bg-cream px-3 py-2">
        <span className="font-medium text-ink">Privacy:</span> this prompt includes {firstName}&rsquo;s first name,
        city and state, grade, academics, activities, affiliations, and citizenship status &mdash; never the
        profile&rsquo;s last name, birth date, address, phone, email, church name, or parent names. It does include
        the organization names you typed for activities, work, and service (an organization matching the
        profile&rsquo;s church name is replaced with &ldquo;their church&rdquo;), the names and sponsors of scholarships
        you already track, dismissed, or have pending, and &mdash; while their toggles are on &mdash; income figures and
        the profile&rsquo;s special circumstances. Read it over before you copy: everything here goes to whichever AI
        service you choose and is covered by that service&rsquo;s privacy policy. For the best local results, run it
        in an AI mode that can browse the web.
      </p>

      {/* Options */}
      <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-3 items-start">
        <div>
          <label className="text-xs text-soft mb-1 block" htmlFor="discovery-item-count">
            Scholarships to find
          </label>
          <Input
            id="discovery-item-count"
            type="number"
            min={DISCOVERY_PROMPT_ITEM_COUNT_MIN}
            max={DISCOVERY_PROMPT_ITEM_COUNT_MAX}
            step={5}
            value={itemCountText}
            onChange={(e) => onItemCountChange(e.target.value)}
            onBlur={onItemCountBlur}
          />
        </div>
        <div className="space-y-2 sm:pt-6">
          <label className="flex items-start gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="mt-0.5 accent-forest-700"
              checked={options.includeFinancial}
              onChange={(e) => setOptions((o) => ({ ...o, includeFinancial: e.target.checked }))}
            />
            <span>
              Include financial info (income bracket / EFC){' '}
              <span className="text-soft">&mdash; improves need-based matching; untick to leave it out.</span>
            </span>
          </label>
          {/* Only offered when the profile actually has special circumstances to share. */}
          {!!profile?.special_circumstances?.trim() && (
            <label className="flex items-start gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="mt-0.5 accent-forest-700"
                checked={options.includeSpecialCircumstances}
                onChange={(e) => setOptions((o) => ({ ...o, includeSpecialCircumstances: e.target.checked }))}
              />
              <span>
                Include special circumstances from the profile{' '}
                <span className="text-soft">&mdash; free text that may be sensitive; untick to leave it out.</span>
              </span>
            </label>
          )}
        </div>
      </div>
      <div>
        <label className="text-xs text-soft mb-1 block" htmlFor="discovery-focus-notes">
          Focus notes (optional)
        </label>
        <Textarea
          id="discovery-focus-notes"
          rows={2}
          value={options.focusNotes}
          onChange={(e) => setOptions((o) => ({ ...o, focusNotes: e.target.value }))}
          placeholder="e.g. prioritize art scholarships; skip anything that needs a video; we belong to the LCMS"
        />
      </div>

      {/* The prompt */}
      {loading ? (
        <p className="text-sm text-soft">Gathering profile, target schools, and your tracked scholarships&hellip;</p>
      ) : (
        <>
          {loadError && (
            <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
              {loadError} The prompt below was built without them.
            </p>
          )}
          {!profile && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              {firstName}&rsquo;s profile is empty, so the prompt has little to match on. Filling in the Profile tab
              first will give much better results.
            </p>
          )}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs text-soft">{wordCount.toLocaleString()} words</span>
            <Button size="sm" onClick={copyPrompt} disabled={!prompt}>
              {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
              {copied ? 'Copied!' : 'Copy prompt'}
            </Button>
          </div>
          {copyError && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">{copyError}</p>
          )}
          {/* Plain textarea (not the ui Textarea) so we can hold a ref for the
              clipboard fallback; same classes as ui/Textarea. */}
          <textarea
            ref={textareaRef}
            readOnly
            rows={16}
            value={prompt}
            className="w-full rounded-md border border-line px-3 py-2 shadow-sm focus:border-forest-600 focus:outline-none focus:ring-1 focus:ring-forest-600 font-mono text-xs leading-relaxed"
            aria-label="Generated discovery prompt"
          />
        </>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Close</Button>
        <Button variant="secondary" onClick={onOpenImport}>
          <Upload size={14} className="mr-1" /> Open Import AI Discoveries
        </Button>
      </div>
    </Card>
  );
}
